/**
 * Authenticated Fetch mit automatischem Token-Refresh
 * ====================================================
 * Wrapper um fetch(), der bei 401 automatisch /api/auth/refresh aufruft
 * und den Request danach wiederholt. Wird für alle Portal-/Admin-Endpunkte
 * verwendet, die ein gültiges JWT-Cookie erfordern.
 */

let refreshPromise: Promise<boolean> | null = null

/**
 * Versucht den Access-Token über den Refresh-Token zu erneuern.
 * De-dupliziert parallele Refresh-Aufrufe.
 */
async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST' })
      return res.ok
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

/**
 * Fetch mit automatischem Token-Refresh bei 401.
 */
export async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  let res = await fetch(url, init)
  if (res.status === 401) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      // Retry mit neuem Cookie
      res = await fetch(url, init)
    }
  }
  return res
}

/**
 * JSON GET mit Auth + Auto-Refresh.
 */
export async function fetchJsonAuth<T>(url: string): Promise<T> {
  const res = await authFetch(url)
  if (!res.ok) throw new Error(`Fehler ${res.status}`)
  return res.json()
}

/**
 * JSON POST/PUT/PATCH mit Auth + Auto-Refresh.
 */
export async function postJsonAuth<T>(url: string, body: unknown, method = 'POST'): Promise<T> {
  const res = await authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ detail: `Fehler ${res.status}` }))
    throw new Error(data.detail || `Fehler ${res.status}`)
  }
  return res.json()
}
